import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeftIcon, ChevronRightIcon, FolderIcon, CpuChipIcon, ChatBubbleLeftRightIcon } from "@heroicons/react/24/outline";
import { getGlobalDashboard, updateCredits } from "../../Requests/TeamRequests";
import { useTeamsContext } from "../../Components/ContextProviders/TeamsContextProvider";
import { useMyContext } from "../../Components/ContextProviders/CreditsContextProvider";
import { useSidebar } from "../../Components/ContextProviders/SidebarContextProvider";
import { getSidebarPadding } from "../../utils/sidebar";
import TasksWidget from "../../Components/Tasks/TasksWidget";
import ConversationsWidget from "../../Components/Conversations/ConversationsWidget";
import UpgradedLoader from "../../Components/UpgradedLoader";
import SectionHeader from "../../Components/GeneralComponents/SectionHeader";

export default function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isCollapsed } = useSidebar();
  const { currentTeam } = useTeamsContext();
  const { setCredits } = useMyContext();

  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [calendarDate, setCalendarDate] = useState(new Date());

  useEffect(() => {
    if (!currentTeam?.uuid) return;
    getGlobalDashboard(currentTeam.uuid)
      .then((res) => {
        setDashboardData(res.data);
        setDashboardLoading(false);
      })
      .catch(() => setDashboardLoading(false));
    updateCredits(currentTeam.uuid)
      .then((res) => setCredits(res.data))
      .catch(() => {});
  }, [currentTeam?.uuid]);

  const goToPreviousMonth = () => {
    const d = new Date(calendarDate);
    d.setMonth(d.getMonth() - 1);
    setCalendarDate(d);
  };

  const goToNextMonth = () => {
    const d = new Date(calendarDate);
    d.setMonth(d.getMonth() + 1);
    setCalendarDate(d);
  };

  const generateBentoCalendar = () => {
    const today = new Date();
    const currentMonth = calendarDate.getMonth();
    const currentYear = calendarDate.getFullYear();
    const monthNames = [
      t('Calendar.MonthNames.January'), t('Calendar.MonthNames.February'),
      t('Calendar.MonthNames.March'), t('Calendar.MonthNames.April'),
      t('Calendar.MonthNames.May'), t('Calendar.MonthNames.June'),
      t('Calendar.MonthNames.July'), t('Calendar.MonthNames.August'),
      t('Calendar.MonthNames.September'), t('Calendar.MonthNames.October'),
      t('Calendar.MonthNames.November'), t('Calendar.MonthNames.December'),
    ];
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
    const days = [];
    for (let i = 0; i < firstDayOfMonth; i++) days.push(null);
    for (let day = 1; day <= daysInMonth; day++) days.push(day);
    return { monthNames, currentMonth, currentYear, today, days };
  };

  const getMeetingDaysInMonth = () => {
    if (!dashboardData?.meetings) return new Set();
    return new Set(
      dashboardData.meetings
        .filter((m) => {
          if (!m.date) return false;
          const d = new Date(m.date);
          return (
            d.getMonth() === calendarDate.getMonth() &&
            d.getFullYear() === calendarDate.getFullYear()
          );
        })
        .map((m) => new Date(m.date).getDate())
    );
  };

  const activeProjects = dashboardData?.projects?.length ?? 0;
  const openTasks = dashboardData?.tasks?.filter(
    (t) => t.status?.name?.toLowerCase() !== "done"
  ).length ?? 0;
  const unreadConversations = dashboardData?.conversations?.reduce(
    (sum, c) => sum + (c.unread_count || 0), 0
  ) ?? 0;

  return (
    <div className={`${getSidebarPadding(isCollapsed)} pt-4 md:pt-12 w-full flex justify-center px-4 transition-all duration-300`}>
      <div className="flex flex-col w-full justify-center items-center gap-4 md:px-4">

        <div className="bg-white max-w-7xl rounded-2xl w-full">
          <SectionHeader title={t('Dashboard.Title')} subtitle={t('Dashboard.Subtitle')} />
        </div>

        {dashboardLoading ? (
          <div className="flex items-center justify-center py-24">
            <UpgradedLoader className="animate-spin" w={8} h={8} bg="bg-black" />
          </div>
        ) : (
          <div className="w-full max-w-7xl mb-8">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-6 lg:grid-rows-2">

              {/* Statistics */}
              <div className="relative lg:col-span-2 group">
                <div className="absolute inset-0 rounded-lg bg-white max-lg:rounded-t-[2rem] lg:rounded-tl-[2rem]" />
                <div className="relative flex h-full flex-col overflow-hidden rounded-[calc(theme(borderRadius.lg)+1px)] max-lg:rounded-t-[calc(2rem+1px)] lg:rounded-tl-[calc(2rem+1px)]">
                  <div className="h-80 bg-white p-8 flex flex-col justify-center gap-6">
                    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                      <div className="w-10 h-10 bg-iavisus-100 rounded-lg flex items-center justify-center shrink-0">
                        <FolderIcon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-900" data-testid="dashboard-stat-projects">{activeProjects}</p>
                        <p className="text-sm text-gray-500">{t('Dashboard.ActiveProjects')}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                      <div className="w-10 h-10 bg-iavisus-100 rounded-lg flex items-center justify-center shrink-0">
                        <CpuChipIcon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-900" data-testid="dashboard-stat-tasks">{openTasks}</p>
                        <p className="text-sm text-gray-500">{t('Dashboard.OpenTasks')}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                      <div className="w-10 h-10 bg-iavisus-100 rounded-lg flex items-center justify-center shrink-0">
                        <ChatBubbleLeftRightIcon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-900" data-testid="dashboard-stat-conversations">{unreadConversations}</p>
                        <p className="text-sm text-gray-500">{t('Dashboard.UnreadConversations')}</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-10 pt-4 flex flex-col flex-1">
                    <p className="mt-2 text-lg font-medium tracking-tight text-gray-950">{t('Dashboard.Statistics')}</p>
                    <p className="mt-2 max-w-lg text-sm/6 text-gray-600">{t('Dashboard.StatisticsDescription')}</p>
                    <div className="mt-auto flex justify-end">
                      <button
                        onClick={() => navigate("/projects")}
                        data-testid="dashboard-all-projects-btn"
                        className="rounded-md bg-iavisus-100 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-iavisus-200"
                      >
                        {t('Dashboard.AllProjects')}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="pointer-events-none absolute inset-0 rounded-lg shadow outline outline-1 outline-black/5 max-lg:rounded-t-[2rem] lg:rounded-tl-[2rem]" />
              </div>

              {/* Calendar with meeting dots */}
              <div className="relative lg:col-span-2">
                <div className="absolute inset-0 rounded-lg bg-white" />
                <div className="relative flex h-full flex-col overflow-hidden rounded-[calc(theme(borderRadius.lg)+1px)]">
                  <div className="h-80 bg-white p-6">
                    {(() => {
                      const { monthNames, currentMonth, currentYear, today, days } = generateBentoCalendar();
                      const meetingDays = getMeetingDaysInMonth();
                      return (
                        <div className="text-center">
                          <div className="flex items-center justify-between mb-4">
                            <button type="button" onClick={goToPreviousMonth} data-testid="dashboard-calendar-prev" className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500">
                              <span className="sr-only">{t('Calendar.PreviousMonth')}</span>
                              <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                            </button>
                            <h2 data-testid="dashboard-calendar-heading" className="font-semibold text-gray-900">{monthNames[currentMonth]} {currentYear}</h2>
                            <button type="button" onClick={goToNextMonth} data-testid="dashboard-calendar-next" className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500">
                              <span className="sr-only">{t('Calendar.NextMonth')}</span>
                              <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                            </button>
                          </div>
                          <div className="grid grid-cols-7 mt-8 text-xs leading-6 text-center text-gray-500">
                            <div>{t('Calendar.DayNames.Sun')}</div>
                            <div>{t('Calendar.DayNames.Mon')}</div>
                            <div>{t('Calendar.DayNames.Tue')}</div>
                            <div>{t('Calendar.DayNames.Wed')}</div>
                            <div>{t('Calendar.DayNames.Thu')}</div>
                            <div>{t('Calendar.DayNames.Fri')}</div>
                            <div>{t('Calendar.DayNames.Sat')}</div>
                          </div>
                          <div className="grid grid-cols-7 mt-2 text-sm">
                            {days.map((day, idx) => {
                              if (day === null) return <div key={idx} />;
                              const isToday =
                                day === today.getDate() &&
                                currentMonth === today.getMonth() &&
                                currentYear === today.getFullYear();
                              const hasMeeting = meetingDays.has(day);
                              return (
                                <div key={day} className="py-1">
                                  <button
                                    type="button"
                                    className={`mx-auto flex h-8 w-8 items-center justify-center rounded-full ${
                                      isToday ? "bg-iavisus-100 font-semibold text-white" : "text-gray-900 hover:bg-gray-200"
                                    }`}
                                  >
                                    <time dateTime={`${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`}>
                                      {day}
                                    </time>
                                  </button>
                                  {hasMeeting && (
                                    <span data-testid={`dashboard-calendar-dot-${day}`} className="block mx-auto mt-0.5 h-1 w-1 rounded-full bg-iavisus-100" />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="p-10 pt-4 flex flex-col flex-1">
                    <p className="mt-2 text-lg font-medium tracking-tight text-gray-950">{t('Dashboard.Calendar')}</p>
                    <p className="mt-2 max-w-lg text-sm/6 text-gray-600">{t('Dashboard.CalendarDescription')}</p>
                    <div className="mt-auto flex justify-end">
                      <button
                        onClick={() => navigate("/calendar")}
                        data-testid="dashboard-open-calendar-btn"
                        className="rounded-md bg-iavisus-100 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-iavisus-200"
                      >
                        {t('Dashboard.OpenCalendar')}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="pointer-events-none absolute inset-0 rounded-lg shadow outline outline-1 outline-black/5" />
              </div>

              {/* Conversations with project badges */}
              <div className="relative lg:col-span-2">
                <div className="absolute inset-0 rounded-lg bg-white lg:rounded-tr-[2rem]" />
                <div className="relative flex h-full flex-col overflow-hidden rounded-[calc(theme(borderRadius.lg)+1px)] lg:rounded-tr-[calc(2rem+1px)]">
                  <div className="h-80 bg-white">
                    <ConversationsWidget
                      project={null}
                      preloadedData={dashboardData?.conversations}
                      showProjectBadge={true}
                    />
                  </div>
                  <div className="p-10 pt-4">
                    <p className="mt-2 text-lg font-medium tracking-tight text-gray-950">{t('Dashboard.Conversations')}</p>
                    <p className="mt-2 max-w-lg text-sm/6 text-gray-600">{t('Dashboard.ConversationsDescription')}</p>
                  </div>
                </div>
                <div className="pointer-events-none absolute inset-0 rounded-lg shadow outline outline-1 outline-black/5 lg:rounded-tr-[2rem]" />
              </div>

              {/* Tasks with project badges */}
              <div className="relative lg:col-span-2">
                <div className="absolute inset-0 rounded-lg bg-white lg:rounded-bl-[2rem]" />
                <div className="relative flex h-full flex-col overflow-hidden rounded-[calc(theme(borderRadius.lg)+1px)] lg:rounded-bl-[calc(2rem+1px)]">
                  <TasksWidget
                    project={null}
                    preloadedData={dashboardData?.tasks}
                    showProjectBadge={true}
                  />
                </div>
                <div className="pointer-events-none absolute inset-0 rounded-lg shadow outline outline-1 outline-black/5 lg:rounded-bl-[2rem]" />
              </div>

              {/* Projects list */}
              <div className="relative lg:col-span-4">
                <div className="absolute inset-0 rounded-lg bg-white max-lg:rounded-b-[2rem] lg:rounded-br-[2rem]" />
                <div className="relative flex h-full flex-col overflow-hidden rounded-[calc(theme(borderRadius.lg)+1px)] max-lg:rounded-b-[calc(2rem+1px)] lg:rounded-br-[calc(2rem+1px)]">
                  <div className="h-80 bg-white p-6 overflow-y-auto">
                    {dashboardData?.projects?.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full text-center">
                        <FolderIcon className="w-10 h-10 text-gray-300 mb-3" />
                        <p className="text-sm text-gray-500">{t('Dashboard.NoProjects')}</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {dashboardData?.projects?.map((project) => (
                          <button
                            key={project.uuid}
                            onClick={() => navigate(`/project/${project.uuid}`)}
                            data-testid={`dashboard-project-${project.uuid}`}
                            className="text-left p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors border border-gray-100 group"
                          >
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 bg-iavisus-100 rounded-lg flex items-center justify-center shrink-0 group-hover:bg-iavisus-200 transition-colors">
                                <FolderIcon className="w-4 h-4 text-white" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{project.name}</p>
                                {project.description && (
                                  <p className="text-xs text-gray-500 truncate mt-0.5">{project.description}</p>
                                )}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="p-10 pt-4 flex flex-col flex-1">
                    <p className="mt-2 text-lg font-medium tracking-tight text-gray-950">{t('Dashboard.AllProjects')}</p>
                    <p className="mt-2 max-w-lg text-sm/6 text-gray-600">{t('Dashboard.AllProjectsDescription')}</p>
                    <div className="mt-auto flex justify-end">
                      <button
                        onClick={() => navigate("/projects")}
                        data-testid="dashboard-manage-projects-btn"
                        className="rounded-md bg-iavisus-100 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-iavisus-200"
                      >
                        {t('Dashboard.ManageProjects')}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="pointer-events-none absolute inset-0 rounded-lg shadow outline outline-1 outline-black/5 max-lg:rounded-b-[2rem] lg:rounded-br-[2rem]" />
              </div>

            </div>
          </div>
        )}

      </div>
    </div>
  );
}
